import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';

export interface AwsConnectionData {
    id: string;
    tenantId: string;
    name: string;
    description?: string;
    accountId: string;
    region: string;
    accessMethod: 'cross-account-role' | 'access-keys';
    connectionType: 'account' | 'service-specific';
    roleArn?: string;
    externalId?: string;
    status: 'active' | 'inactive' | 'error';
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
}

interface CreateAwsConnectionData {
    tenantId: string;
    name: string;
    description?: string;
    accountId: string;
    region: string;
    accessMethod: 'cross-account-role';
}

interface UpdateAwsConnectionData {
    connectionId: string;
    tenantId: string;
    roleArn: string;
}

interface CreateAwsConnectionResponse {
    success: boolean;
    connection: AwsConnectionData;
    cloudFormationTemplate: string;
}

export const useAwsConnections = () => {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: ['awsConnections'],
        queryFn: async () => {
            try {
                const response = await apiClient.get('/configurations/connections/aws');
                
                if (!response.ok) {
                    return [];
                }
                
                const data = await response.json();
                return data.connections || [];
            } catch (error) {
                return [];
            }
        }
    });
};

export const useAwsConnection = (connectionId: string) => {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: ['awsConnection', connectionId],
        queryFn: async () => {
            const response = await apiClient.get(`/configurations/connections/aws/${connectionId}`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch AWS connection: ${response.statusText}`);
            }
            
            const data = await response.json();
            return data.connection as AwsConnectionData;
        },
        enabled: !!connectionId
    });
};

export const useCreateAwsConnection = () => {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async (data: CreateAwsConnectionData): Promise<CreateAwsConnectionResponse> => {
            const response = await apiClient.post('/configurations/connections/aws', data);
            
            if (!response.ok) {
                throw new Error(`Failed to create AWS connection: ${response.statusText}`);
            }
            
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['awsConnections'] });
            queryClient.invalidateQueries({ queryKey: ['awsConnection'] });
        }
    });
};

export const useUpdateAwsConnection = () => {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async (data: UpdateAwsConnectionData) => {
            const { connectionId, ...updateData } = data;
            const response = await apiClient.put(`/configurations/connections/aws/${connectionId}`, updateData);
            
            if (!response.ok) {
                throw new Error(`Failed to update AWS connection: ${response.statusText}`);
            }
            
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['awsConnections'] });
            queryClient.invalidateQueries({ queryKey: ['awsConnection'] });
        }
    });
};

export const useTestAwsConnection = () => {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ connectionId }: { connectionId: string }) => {
            const response = await apiClient.post(`/configurations/connections/aws/${connectionId}/test`);
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || data.message || `Connection test failed: ${response.statusText}`);
            }
            
            return data;
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['awsConnections'] });
            queryClient.invalidateQueries({ queryKey: ['awsConnection'] });
        }
    });
};

export const useDeleteAwsConnection = () => {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ connectionId }: { connectionId: string }) => {
            const response = await apiClient.delete(`/configurations/connections/aws/${connectionId}`);
            
            if (!response.ok) {
                throw new Error(`Failed to delete AWS connection: ${response.statusText}`);
            }
            
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['awsConnections'] });
            queryClient.invalidateQueries({ queryKey: ['awsConnection'] });
        }
    });
};

export const useAwsConnectionCloudFormation = () => {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ connectionId }: { connectionId: string }) => {
            const response = await apiClient.get(`/configurations/connections/aws/${connectionId}/cloudformation`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch CloudFormation template: ${response.statusText}`);
            }
            
            return response.text();
        }
    });
};

export const useAwsConnectionTerraform = () => {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ connectionId }: { connectionId: string }) => {
            const response = await apiClient.get(`/configurations/connections/aws/${connectionId}/terraform`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch Terraform template: ${response.statusText}`);
            }
            
            return response.text();
        }
    });
};